import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import { forgotPassword } from '@api/auth';
import ForgotPaswordImg from '@assets/images/forgot-password-img.png';
import useWindowWidth from '@hooks/useWindowWidth';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import NewErrorSummary from '@components/NewErrorSummary';
import {
  updateErrors,
  getErrorMessage as getErrorMessageFromErrors,
} from '@utils/commons';
import getErrorMessage from '@utils/errorUtils';
import './styles.scss';
import validateLoginEmail from '../login/utils';

function ForgotPasswordPage() {
  const [validateErrorMessage, setValidateErrorMessage] = useState<
    string | undefined
  >('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FormError[]>([]);
  const width = useWindowWidth();
  const [sendEmailStatus, setSendEmailStatus] = useState<SubmitStatus>({
    loading: false,
    submitted: SubmitStatusEnum.IDLE,
    message: '',
  });

  const onSubmit = async (event: any) => {
    event.preventDefault();

    let validationErrors: FormError[] = [];
    validationErrors = updateErrors(
      validationErrors,
      'email',
      validateLoginEmail(email)
    );

    setErrors(prev => validationErrors);
    if (validationErrors.length !== 0) {
      return;
    }
    setSendEmailStatus(prev => ({ ...prev, loading: true }));

    try {
      const response = await forgotPassword(email.trim());
      if (response && response.status === 'success') {
        setSendEmailStatus(prev => ({
          ...prev,
          submitted: SubmitStatusEnum.SUCCESS,
          message:
            'Please check your email inbox. You will find a new email from MyWishes. Please open it and reset your password.',
        }));
        setEmail('');
        setTimeout(() => {
          setSendEmailStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.IDLE,
            message: '',
          }));
        }, 3000);
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      setSendEmailStatus({
        loading: false,
        submitted: SubmitStatusEnum.FAILED,
        message: errorMessage,
      });
    } finally {
      setSendEmailStatus(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    setValidateErrorMessage(getErrorMessageFromErrors(errors, 'email'));
  }, [errors]);

  return (
    <div className="govuk-width-container password-reset-page mt-5 mb-5 md:mb-10">
      <h1 className="govuk-heading-l">Password reset</h1>
      <div className="govuk-grid-row">
        {sendEmailStatus.submitted === SubmitStatusEnum.SUCCESS && (
          <SuccessNotificationBanner
            title={sendEmailStatus.message}
            duration={3000}
          />
        )}

        <div className="login-form govuk-grid-row">
          {width < 641 && (
            <div className="govuk-grid-column-one-half mb-3">
              <img
                src={ForgotPaswordImg}
                alt="Password reset illustration"
                className="govuk-image"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          )}
        </div>
        <div className="govuk-grid-column-one-half">
          <p className="govuk-body">
            Enter the email address linked to your MyWishes Care account below.
            Once submitted we will send you an email to reset your password. If
            you cannot see an email from us within 5 minutes, please search in
            your SPAM folder. For further technical support please email{' '}
            <a href="mailto:support@mywishes.co.uk" className="govuk-link">
              support@mywishes.co.uk
            </a>
          </p>
          <p className="govuk-body">
            *Please be aware that this section only resets MyWishes Care
            passwords. MyWishes Care is for health and social care
            professionals. If you are a member of the general public and have a
            general &apos;MyWishes&apos; account, please visit{' '}
            <a
              href="https://www.MyWishes.co.uk"
              className="govuk-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.MyWishes.co.uk
            </a>
          </p>

          <form>
            {errors.length > 0 && <NewErrorSummary errors={errors} />}
            {sendEmailStatus.submitted === SubmitStatusEnum.FAILED && (
              <NewErrorSummary
                errors={[
                  {
                    id: 'sendEmailToResetPassword',
                    message: sendEmailStatus.message,
                  },
                ]}
              />
            )}
            <div
              className={`govuk-form-group govuk-!-margin-bottom-2 ${validateErrorMessage ? 'govuk-form-group--error' : ''}`}
            >
              <label
                className="govuk-label govuk-!-font-weight-bold"
                htmlFor="email"
              >
                Email Address
              </label>

              {validateErrorMessage && (
                <span className="govuk-error-message">
                  <span className="govuk-visually-hidden">Error:</span>{' '}
                  {validateErrorMessage}
                </span>
              )}
              <input
                className={`govuk-input ${validateErrorMessage ? 'govuk-input--error' : ''}`}
                id="email"
                name="email"
                type="email"
                placeholder="Enter your Email Address"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            <button
              type="submit"
              className="govuk-button govuk-!-width-full"
              disabled={sendEmailStatus.loading}
              onClick={onSubmit}
            >
              {sendEmailStatus.loading ? 'Sending email...' : 'Reset Password'}
            </button>
            <div className="govuk-body text-center back-to-login-text">
              <Link to="/login" className="govuk-link">
                Back to login
              </Link>
            </div>
          </form>
        </div>
        {width >= 641 && (
          <div className="govuk-grid-column-one-half">
            <img
              src={ForgotPaswordImg}
              alt="Password reset illustration"
              className="govuk-image"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
